import type { ComponentProps } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "../../lib/utils"

// The one month-grid primitive. Wraps react-day-picker and maps every one of its
// UI slots onto this app's tokens, so a calendar carries the same pill/stone
// vocabulary as the buttons and segmented controls next to it. Deliberately no
// `react-day-picker/style.css` import: the vendor sheet is global and would land
// outside the Tailwind layer, where it wins arbitrary specificity fights.
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("relative p-3", className)}
      // A day is one flat cell; the range fill is drawn by the cell background so
      // start/middle/end join into one continuous band with no gaps between rows.
      classNames={{
        months: "flex flex-col gap-4 sm:flex-row",
        month: "relative flex flex-col gap-3",
        month_caption: "flex h-7 items-center justify-center",
        caption_label: "text-[13px] font-semibold tracking-tight",
        // Overlaid on the caption row rather than laid out beside it, so the month
        // label stays optically centred on the grid below it.
        nav: "absolute inset-x-0 top-0 flex h-7 items-center justify-between",
        button_previous:
          "inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:pointer-events-none disabled:opacity-30",
        button_next:
          "inline-flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground disabled:pointer-events-none disabled:opacity-30",
        chevron: "size-4",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "w-8 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70",
        week: "mt-1 flex w-full",
        day: "size-8 p-0 text-center text-[13px] tabular-nums",
        day_button:
          "inline-flex size-8 items-center justify-center rounded-full outline-none transition-colors hover:bg-foreground/5 focus-visible:ring-2 focus-visible:ring-ring/40",
        selected: "bg-muted",
        range_start:
          "rounded-l-full bg-primary [&>button]:bg-primary [&>button]:text-primary-foreground",
        range_end:
          "rounded-r-full bg-primary [&>button]:bg-primary [&>button]:text-primary-foreground",
        range_middle: "bg-muted [&>button]:hover:bg-foreground/5",
        today: "font-semibold text-brand",
        outside: "text-muted-foreground/40",
        disabled: "pointer-events-none text-muted-foreground/25",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName, ...chevronProps }) =>
          orientation === "left" ? (
            <ChevronLeft className={cn("size-4", chevronClassName)} {...chevronProps} />
          ) : (
            <ChevronRight className={cn("size-4", chevronClassName)} {...chevronProps} />
          ),
      }}
      {...props}
    />
  )
}
